import React, { 
  useCallback, 
  useEffect, 
  useRef, 
  useState, 
  useMemo, 
} from 'react';
import {
  Box,
  Stack
} from '@mui/material';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Youtube from '@tiptap/extension-youtube';
import Code from '@tiptap/extension-code';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import CharacterCount from '@tiptap/extension-character-count';
import TextAlign from '@tiptap/extension-text-align';
import { all, createLowlight } from "lowlight";
import './editor-style.css';
import { EditorContent, useEditor, Editor } from '@tiptap/react';
import axios from 'axios';
import 'highlight.js/styles/vs2015.css';
import 'highlight.js/styles/github.css';
import 'highlight.js/styles/atom-one-dark.css';
import { CustomizedFontSize } from './custom-extensions/font-size';
import Page from './interfaces/page';
import PagesTab from './note-components/pages-tab';
import ResourcesTab from './note-components/resources-tab';
import RightActionTab from './note-components/right-action-tab';
import ActionBar from './note-components/action-bar';
import { data, useParams, useSearchParams } from 'react-router-dom';
import FontFamily from '@tiptap/extension-font-family';
import NoteActionTab from './note-components/note-action-tab';
import BackdropLoader from '../../../../components/backdrop-loader';
import { useSnackbar } from '../../../../components/snackbar';
import { PageBreak } from './custom-extensions/page-break';
import { JSONContent } from '@tiptap/react';
import { Fragment } from 'prosemirror-model';
import { uploadResource } from '../../../../utils/upload-resource';


type RouteParams = {
  classroom_id: string;
  collection_id: string;
  resource_title: string;
};

export default function NoteEditor({}) {
  const { classroom_id, resource_title } = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const resource_url = searchParams.get("id");

  const [loading, setLoading] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pages, setPages] = useState<Page[]>([]);
  const [noteZoom, setNoteZoom] = useState(1);
  const [mode, setMode] = useState(1);
  const [thirdBox, setThirdBox] = useState({ visible: false, value: 0 });


  const [saved, setSaved] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const lowlight = useMemo(() => createLowlight(all), []);


  const autoSaveTimeOut = useRef<ReturnType<typeof setTimeout> | null>(null)

  const pageBreakTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const isProgrammaticScroll = useRef(false);



  const { showMessage } = useSnackbar();


  const editorExtensions = useMemo<any[]>(() => [
    StarterKit.configure({
      blockquote: false,
      codeBlock: false,
      code: false,
    }),
    TextStyle.configure({
      mergeNestedSpanStyles: true
    }),
    Blockquote.configure({
      HTMLAttributes: {
        class: 'blockquote'
      }
    }),
    Color,
    Underline,
    Link.configure({ openOnClick: false }),
    Image.configure({
      HTMLAttributes: {
        class: 'image'
      }
    }),
    Table.configure({
      resizable: true,
      allowTableNodeSelection: true,
      HTMLAttributes: {
        class: 'table'
      }
    }),
    TableRow,
    TableCell,
    TableHeader,
    CodeBlockLowlight.configure({
      lowlight,
      languageClassPrefix: 'language-',
      HTMLAttributes: {
        class: 'codeblock'
      },
      defaultLanguage: null,
    }),
    Youtube.configure({
      controls: false,
      nocookie: true,
      HTMLAttributes: {
        class: 'youtube'
      }
    }),
    Code.configure({
      HTMLAttributes: {
        class: 'code'
      }
    }), 
    Highlight.configure({
      multicolor: true,
      HTMLAttributes: {
        class: 'highlight'
      }
    }),
    Subscript,
    Superscript,
    CharacterCount,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    FontFamily.configure({
      types: ['textStyle']
    }),
    CustomizedFontSize,
    PageBreak
  ], [lowlight]);

  const editor = useEditor({
    extensions: editorExtensions,
    autofocus: true,
    editorProps: {
      attributes: {
        class: mode === 1 ? 'editor-light' : 'editor-dark'
      },
    },
    onUpdate: extractPagesFromEditor,
  });

  function extractPagesFromEditor() {
    if (!editor) return [];

    const dom = editor.view.dom;
    const state = editor.state;
    const children = Array.from(dom.childNodes);
    const docNodes = state.doc.content.content;

    const pages: Page[] = [];
    let currentPageNodes: any[] = [];
    let currentPageHeight = 0;
    let pageIndex = 0;
    const pageBreakNodeType = state.schema.nodes.pageBreak;

    for (let i = 0; i < children.length; i++) {
      const domNode = children[i];
      const pmNode = docNodes[i];
      if (!(domNode instanceof HTMLElement) || !pmNode) continue;
      
      if (pmNode.type === pageBreakNodeType) {
            continue;
        }
      const nodeHeight = domNode.getBoundingClientRect().height;

      currentPageHeight += nodeHeight;
      currentPageNodes.push(pmNode);

      if (currentPageHeight >= 1056) {
        pages.push({
          id: pageIndex++,
          content: { type: 'doc', content: currentPageNodes.map(n => n.toJSON()) },
          title: `Page ${pageIndex}`,
          height: currentPageHeight,
        });

        currentPageNodes = [];
        currentPageHeight = 0;
      }
    }

    if (currentPageNodes.length > 0) {
      pages.push({
        id: pageIndex,
        content: { type: 'doc', content: currentPageNodes.map(n => n.toJSON()) },
        title: `Page ${pageIndex + 1}`,
        height: currentPageHeight,
      });
    }

    setPages(pages)
    

    handleEditorUpdate(pages);


    debouncedAutoSave();
  }

  const handleSave = async () => {
    setIsSaving(true);
    const path = `classrooms/${classroom_id}/resources/${resource_url}`;
    await uploadResource(path, JSON.stringify(pages), "application/json");
    setIsSaving(false);
    setSaved(true);
  };

  const debouncedAutoSave = () => {
    if (autoSaveTimeOut.current) {
      clearTimeout(autoSaveTimeOut.current)
    }

     pageBreakTimeout.current = setTimeout(() => {
      setSaved(false);
    }, 300)
    
  }

  const handleEditorUpdate = (locPages: Page[] | null) => {
    if (!editor || !locPages) return;
    
    if (pageBreakTimeout.current) {
      clearTimeout(pageBreakTimeout.current)
    }

    pageBreakTimeout.current = setTimeout(() => {
      if (locPages.length !== pages.length) {
        const oldSelection = editor.view.state.selection;
        const oldSelectionPos = oldSelection.from;

        const docContent = locPages.flatMap((page, index) => {
          const nodes = page.content.content!.map(node => editor.schema.nodeFromJSON(node));
          
          if (index < locPages.length - 1) {
              nodes.push(editor.schema.nodes.pageBreak.create());
          }
          
          return nodes;
        });
      
        const fragment = Fragment.fromArray(docContent);
        const newDoc = editor.schema.topNodeType.createAndFill(null, fragment);;
        
        if (newDoc) {
            editor.commands.setContent(newDoc.toJSON(), false);

            requestAnimationFrame(() => {
              const pos = Math.min(oldSelectionPos, editor.state.doc.content.size - 1);
              editor.commands.setTextSelection(pos);
            });
        }

        const breaks = document.querySelectorAll<HTMLDivElement>('[data-page-break]');
          breaks.forEach((el, index) => {  
            el.setAttribute('id', `page-break-${index}`);
        });
      }
    }, 10)
  };


  useEffect(() => {
    const fetchNote = async () => {
      try {
        setLoading(true);
        const response = await axios.get<Page[]>(
          `https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${resource_url}`
        );

        if (response.data) {
          setPages(response.data);
        }

        handleEditorUpdate(response.data);
      } catch (error) {
        console.error();
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [classroom_id, resource_title]);


  useEffect(() => {
    const el = document.getElementById(`page-break-${currentPageIndex}`);
    if (el) {
      isProgrammaticScroll.current = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 100);
    }
  }, [currentPageIndex]);

  useEffect(() => {
  if (!scrollContainerRef.current) return;
  
  const observer = new IntersectionObserver(
    (entries) => {
      if (isProgrammaticScroll.current) return;
      
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      
      if (visible.length > 0) {
        const id = visible[0].target.id;
        const match = id.match(/page-break-(\d+)/);
        if (match) {
          const pageIndex = parseInt(match[1], 10);
          setCurrentPageIndex(prev => (prev !== pageIndex ? pageIndex : prev));
        }
      }
    },
    { 
      
      threshold: 0.5,
      rootMargin: '0px 0px -20% 0px' 
    }
  );

    const pageBreaks = document.querySelectorAll('[data-page-break]');
    pageBreaks.forEach((el) => observer.observe(el));
    
    return () => observer.disconnect();
  }, [pages]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

      if (
          (isMac && event.metaKey && event.key === 's') ||
          (!isMac && event.ctrlKey && event.key === 's')
      ) {
          event.preventDefault();
          handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
        window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSave]);



  if (!editor || loading) {
    return (
      <BackdropLoader/>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: mode === 1 ? '#c6e1f5' : "#121212",
    }}>

      <Stack padding={1.5} spacing={1}>
        <ActionBar
          mode={mode}
          title={resource_title!}
          classroom_id={classroom_id!}
          resource_url={resource_url!}
          pages={pages}
          extensions={editorExtensions}
          saved={saved}
          isSaving={isSaving}
          onSave={handleSave}
        />

        <NoteActionTab 
          editor={editor} 
          mode={mode} 
        />
      </Stack>

      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        padding: 1,
        gap: 1,
        height: '100%'
      }}>
        <PagesTab 
          mode={mode} 
          pages={pages} 
          currentPageIndex={currentPageIndex}
          setCurrentPageIndex={setCurrentPageIndex}
        />
          <Box
            ref={scrollContainerRef}
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingBottom: 3,
            }}
          >
            <Box
              sx={{
                transform: `scale(${noteZoom})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease-in-out',
                width: `${100 / noteZoom}%`,
              }}
            >
              <EditorContent editor={editor} />
            </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: '#000000',
            height: '300px'
          }}
        >

        </Box>

        <ResourcesTab mode={mode} thirdBox={thirdBox} setThirdBox={setThirdBox}/>

        <RightActionTab editor={editor} mode={mode} setMode={setMode} noteZoom={noteZoom} 
          setNoteZoom={setNoteZoom} thirdBox={thirdBox} setThirdBox={setThirdBox}/>

      </Box>
    </Box>
  );
}